import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, PartyPopper } from 'lucide-react';
import { EggHatchAnimation } from './EggHatchAnimation';
const INTEREST_OPTIONS = ['Coffee & Tea', 'Fast Food', 'Healthy Eats', 'Desserts', 'Local Favorites'];
const REFERRAL_SOURCES = ['Instagram', 'TikTok', 'WhatsApp', 'A friend', 'Search', 'Other'];
// To save data: in Google Forms use "Get pre-filled link", swap /viewform for
// /formResponse below, and paste each entry.XXXX id into FORM_ENTRIES.
const GOOGLE_FORM_URL = 'https://docs.google.com/forms/d/e/REPLACE_WITH_YOUR_FORM_ID/formResponse';
const FORM_ENTRIES = {
  name: 'entry.REPLACE_NAME',
  email: 'entry.REPLACE_EMAIL',
  phone: 'entry.REPLACE_PHONE',
  referralSource: 'entry.REPLACE_SOURCE',
  interests: 'entry.REPLACE_INTERESTS',
  referredBy: 'entry.REPLACE_INVITE_CODE'
};
interface SignupData {
  name: string;
  email: string;
  phone: string;
  referralSource: string;
  interests: string[];
  referredBy: string;
}
interface SignupStateProps {
  onJoin: (data: SignupData) => void;
}
export function SignupState({
  onJoin
}: SignupStateProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [referralSource, setReferralSource] = useState('');
  const [interests, setInterests] = useState<string[]>([]);
  const [referredBy, setReferredBy] = useState('');
  const [errors, setErrors] = useState({
    name: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Capture the inviter's referral code from the URL (e.g. ?ref=KRAYAARJ482).
  // This is how a referral is attributed: the code rides in the share link,
  // we read it on load, carry it silently through signup, and submit it so the
  // backend can credit the inviter and tally how many friends they brought in.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) setReferredBy(ref.trim());
  }, []);
  const toggleInterest = (interest: string) => {
    setInterests(prev => prev.includes(interest) ? prev.filter(i => i !== interest) : [...prev, interest]);
  };
  // Fire-and-forget submission to Google Forms. We use mode: 'no-cors' because
  // Google's endpoint doesn't return CORS headers — the request still lands,
  // we just can't read the response (which is fine for collecting entries).
  const submitToGoogleForm = (data: SignupData) => {
    if (GOOGLE_FORM_URL.includes('REPLACE')) return; // not configured yet
    try {
      const body = new URLSearchParams();
      body.append(FORM_ENTRIES.name, data.name);
      body.append(FORM_ENTRIES.email, data.email);
      body.append(FORM_ENTRIES.phone, data.phone);
      body.append(FORM_ENTRIES.referralSource, data.referralSource);
      body.append(FORM_ENTRIES.referredBy, data.referredBy);
      // Checkbox questions accept the same entry id repeated once per choice.
      data.interests.forEach(interest => body.append(FORM_ENTRIES.interests, interest));
      fetch(GOOGLE_FORM_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: body.toString()
      }).catch(() => {
        /* swallow — best-effort sync */
      });
    } catch {
      /* never block the user's signup on a sync failure */
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = {
      name: '',
      email: ''
    };
    let hasError = false;
    if (!name.trim()) {
      newErrors.name = 'What should we call you?';
      hasError = true;
    }
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      newErrors.email = 'Please enter a valid email.';
      hasError = true;
    }
    setErrors(newErrors);
    if (!hasError) {
      setIsSubmitting(true);
      const data: SignupData = {
        name,
        email,
        phone,
        referralSource,
        interests,
        referredBy
      };
      // Save the entry to Google Forms (no-op until GOOGLE_FORM_URL is set),
      // then advance to the success screen.
      submitToGoogleForm(data);
      setTimeout(() => {
        onJoin(data);
      }, 600);
    }
  };
  return <motion.div className="w-full max-w-md mx-auto flex flex-col items-center text-center" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    scale: 0.95,
    filter: 'blur(4px)'
  }} transition={{
    duration: 0.4,
    ease: 'easeOut'
  }} data-id="element-88">
      <EggHatchAnimation data-id="element-89" />

      <motion.div className="relative mb-8" initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} transition={{
      type: 'spring',
      delay: 0.2,
      bounce: 0.5
    }} data-id="element-90">
        <div className="inline-flex items-center gap-2 bg-[#E2F0A8] text-black px-5 py-2.5 rounded-full font-bold text-sm border-4 border-black neo-shadow" data-id="element-91">
          <Sparkles className="w-4 h-4 text-lime-600" aria-hidden="true" data-id="element-92" />
          Get Ready for Early Access
        </div>
      </motion.div>

      <h2 className="text-5xl md:text-6xl font-bold leading-[1.1] mb-4 uppercase tracking-tight" data-id="element-93">
        Get perks at your local spots.
      </h2>

      <p className="text-lg font-medium mb-8 px-4 opacity-90" data-id="element-94">
        India's #1 coffee social app. Join the exclusive IRL Coffee Club before
        your friends do.
      </p>

      <form onSubmit={handleSubmit} className="w-full space-y-4 mb-8" data-id="element-95">
        {referredBy && <div className="flex items-center gap-3 bg-[#E2F0A8] border-4 border-black rounded-2xl px-4 py-3 text-left neo-shadow" data-id="element-96">
            <PartyPopper className="w-5 h-5 flex-shrink-0 text-lime-700" aria-hidden="true" data-id="element-97" />
            <p className="text-sm font-bold leading-tight" data-id="element-98">
              You were invited! Code{' '}
              <span className="font-mono tracking-wide" data-id="element-99">{referredBy}</span> is
              applied — you'll both climb the line.
            </p>
          </div>}

        <div className="space-y-1 text-left" data-id="element-100">
          <input type="text" placeholder="First Name" value={name} onChange={e => setName(e.target.value)} disabled={isSubmitting} className={`w-full px-5 py-4 rounded-2xl border-4 outline-none transition-all text-lg font-medium
              ${errors.name ? 'border-red-500 bg-red-50' : 'border-black bg-white focus:neo-shadow'}
              disabled:opacity-50`} data-id="element-101" />
          {errors.name && <p className="text-red-600 font-medium text-sm px-2" data-id="element-102">
              {errors.name}
            </p>}
        </div>

        <div className="space-y-1 text-left" data-id="element-103">
          <input type="email" placeholder="Email Address" value={email} onChange={e => setEmail(e.target.value)} disabled={isSubmitting} className={`w-full px-5 py-4 rounded-2xl border-4 outline-none transition-all text-lg font-medium
              ${errors.email ? 'border-red-500 bg-red-50' : 'border-black bg-white focus:neo-shadow'}
              disabled:opacity-50`} data-id="element-104" />
          {errors.email && <p className="text-red-600 font-medium text-sm px-2" data-id="element-105">
              {errors.email}
            </p>}
        </div>

        <div className="space-y-1 text-left" data-id="element-106">
          <label htmlFor="phone" className="block font-bold text-sm px-2 mb-1" data-id="element-107">
            Phone Number{' '}
            <span className="font-medium opacity-60" data-id="element-108">(Optional)</span>
          </label>
          <input id="phone" type="tel" placeholder="Enter your phone number" value={phone} onChange={e => setPhone(e.target.value)} disabled={isSubmitting} className="w-full px-5 py-4 rounded-2xl border-4 border-black bg-white outline-none transition-all text-lg font-medium focus:neo-shadow disabled:opacity-50" data-id="element-109" />
        </div>

        <div className="space-y-1 text-left" data-id="element-110">
          <label htmlFor="referralSource" className="block font-bold text-sm px-2 mb-1" data-id="element-111">
            How did you hear about us?
          </label>
          <select id="referralSource" value={referralSource} onChange={e => setReferralSource(e.target.value)} disabled={isSubmitting} className="w-full px-5 py-4 rounded-2xl border-4 border-black bg-white outline-none transition-all text-lg font-medium focus:neo-shadow disabled:opacity-50 appearance-none cursor-pointer" data-id="element-112">
            <option value="" data-id="element-113">Select an option</option>
            {REFERRAL_SOURCES.map(source => <option key={source} value={source} data-id="element-114">
                {source}
              </option>)}
          </select>
        </div>

        <div className="space-y-2 text-left" data-id="element-115">
          <span className="block font-bold text-base px-2 mb-2" data-id="element-116">
            What interests you?{' '}
            <span className="font-semibold text-black/70" data-id="element-117">
              (Select all that apply)
            </span>
          </span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-118">
            {INTEREST_OPTIONS.map(interest => {
            const checked = interests.includes(interest);
            return <button key={interest} type="button" role="checkbox" aria-checked={checked} onClick={() => toggleInterest(interest)} disabled={isSubmitting} className={`flex items-center gap-3 px-4 py-3.5 rounded-2xl border-4 border-black font-bold text-base transition-all disabled:opacity-50
                    ${checked ? 'bg-kraya-yellow neo-shadow' : 'bg-white hover:neo-shadow'}`} data-id="element-119">
                  <span className={`w-6 h-6 flex-shrink-0 rounded-md border-2 border-black flex items-center justify-center transition-colors
                      ${checked ? 'bg-black text-kraya-yellow' : 'bg-white'}`} data-id="element-120">
                    {checked && <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4" aria-hidden="true" data-id="element-121">
                        <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-122" />
                      </svg>}
                  </span>
                  {interest}
                </button>;
          })}
          </div>
        </div>

        <div className="space-y-1 text-left" data-id="element-123">
          <label htmlFor="inviteCode" className="block font-bold text-sm px-2 mb-1" data-id="element-124">
            Invite Code{' '}
            <span className="font-medium opacity-60" data-id="element-125">(Optional)</span>
          </label>
          <input id="inviteCode" type="text" placeholder="Got a code from a friend?" value={referredBy} onChange={e => setReferredBy(e.target.value.toUpperCase())} disabled={isSubmitting} className="w-full px-5 py-4 rounded-2xl border-4 border-black bg-white outline-none transition-all text-lg font-mono tracking-wide uppercase focus:neo-shadow disabled:opacity-50 placeholder:font-sans placeholder:normal-case placeholder:tracking-normal" data-id="element-126" />
        </div>

        <motion.button type="submit" disabled={isSubmitting} whileHover={{
        y: -2
      }} whileTap={{
        y: 2,
        scale: 0.98
      }} className="w-full bg-black text-white text-xl font-bold py-4 rounded-2xl border-4 border-black neo-shadow hover:neo-shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:hover:neo-shadow" data-id="element-127">
          {isSubmitting ? <motion.div animate={{
          rotate: 360
        }} transition={{
          repeat: Infinity,
          duration: 1,
          ease: 'linear'
        }} className="w-6 h-6 border-4 border-white border-t-transparent rounded-full" data-id="element-128" /> : <>
              Claim my spot ☕ <ArrowRight className="w-6 h-6" data-id="element-129" />
            </>}
        </motion.button>
      </form>

      <div className="flex items-center gap-3 bg-white/50 backdrop-blur-sm px-5 py-3 rounded-full border-2 border-black font-medium text-sm" data-id="element-130">
        <div className="flex -space-x-2" data-id="element-131">
          {[1, 2, 3].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-black bg-kraya-yellow flex items-center justify-center text-xs overflow-hidden" data-id="element-132">
              <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="User" className="w-full h-full object-cover" data-id="element-133" />
            </div>)}
        </div>
        <span data-id="element-134">
          <strong className="font-bold" data-id="element-135">2,300+</strong> coffee lovers in line
        </span>
      </div>
    </motion.div>;
}